import React from 'react'
import { connect } from 'react-redux'
import Button from 'material-ui/Button'
import { withStyles } from 'material-ui/styles'
import Icon from 'material-ui/Icon'
import TextField from 'material-ui/TextField'
import MenuItem from 'material-ui/Menu/MenuItem'
import styles from './styles-detail'
import { Provider, TableView, TableHeader, Column, Row, TableBody, Cell, AlertDialog, DialogTrigger, ActionButton, Dialog, Heading, Header, Divider, Content as SpectrumContent, Text, ButtonGroup, Button as SpectrumButton } from '@adobe/react-spectrum'
import { customSpectrumTheme, SPECTRUM_THEME_CLASS } from '../../themes/spectrum-theme'

import Title from '../ui/title'
import Content from '../ui/content'
import Back from '../ui/back'
import Breadcrumbs from '../ui/breadcrumbs'

import {
  getFeatureDefinitions,
  grantTrialFeature,
  revokeTrialFeature,
  getOrganisationDetails,
  clearFeatureError
} from '../../redux/actions/organisationDetailActions'

class OrganisationDetail extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedFeature: '',
      selectedDays: 14,
      selectedAccessType: 'trial'
    }
  }

  componentDidMount() {
    const { dispatch, organisationDetail } = this.props

    // Only load feature definitions if not already loaded
    if (!organisationDetail.featureDefinitions || organisationDetail.featureDefinitions.length === 0) {
      dispatch(getFeatureDefinitions())
    }
  }

  handleGrantFeature = async () => {
    const { selectedFeature, selectedDays, selectedAccessType } = this.state
    const { organisation, dispatch } = this.props

    if (!selectedFeature) {
      return
    }

    dispatch(grantTrialFeature(organisation.id, selectedFeature, selectedDays, selectedAccessType === 'indefinite'))
    this.setState({ selectedFeature: '', selectedDays: 14, selectedAccessType: 'trial' })

    // Refresh organisation details after 500ms to allow backend to process
    setTimeout(() => {
      dispatch(getOrganisationDetails(organisation.id))
    }, 500)
  }

  handleConfirmRevoke = (featureName) => {
    const { organisation, dispatch } = this.props

    dispatch(revokeTrialFeature(organisation.id, featureName))

    // Refresh organisation details after 500ms to allow backend to process
    setTimeout(() => {
      dispatch(getOrganisationDetails(organisation.id))
    }, 500)
  }

  formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  getStatusColor = (isActive) => {
    return isActive ? '#4caf50' : '#f44336'
  }

  renderPreferenceValue = value => {
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : '-'
    }

    return value || '-'
  }

  render() {
    const { organisation, classes, onBack, translate } = this.props
    const organisationDetail = this.props.organisationDetail || {}
    const { featureDefinitions = [], error = null, grantingFeature = false } = organisationDetail
    const { selectedFeature, selectedDays, selectedAccessType } = this.state

    if (!organisation) {
      return <div>{translate ? translate('loading') : 'Loading...'}</div>
    }

    // Group features from organisation response by category
    const enrichedFeatures = {}

    if (organisation.features && Array.isArray(organisation.features)) {
      organisation.features.forEach(feature => {
        const category = feature.category || 'Other'
        if (!enrichedFeatures[category]) {
          enrichedFeatures[category] = []
        }

        enrichedFeatures[category].push({
          name: feature.featureName,
          displayName: feature.displayName,
          category: feature.category,
          isActive: feature.isActive,
          expiryDate: feature.expiryDate,
          daysRemaining: feature.daysRemaining,
          reason: feature.reason
        })
      })
    }

    return (


      <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
        <div>
          <Title
            title={translate('organisation_details')}
            back={
              <Breadcrumbs
                items={[
                  { label: translate('dashboard_title'), link: '/dashboard' },
                  { label: organisation.name }
                ]}
              />
            }
          />
          <Content translate={translate}>
            <div className={classes.container}>
            {/* Header Section */}
            <div className={classes.headerSection} style={{ 
              borderBottom: '1px solid #e0e0e0', 
              paddingBottom: '20px',
              marginBottom: '30px'
            }}>
              <div className={classes.headerTitle}>
                <h2 style={{ margin: 0 }}>{organisation.name}</h2>
                <span style={{
                  backgroundColor: organisation.subscriptionStatus?.isActive ? '#e8f5e9' : '#fff3e0',
                  color: this.getStatusColor(organisation.subscriptionStatus?.isActive),
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-block',
                  marginTop: '8px'
                }}>
                  {organisation.subscriptionStatus?.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Main Details Grid */}
            <div className={classes.gridContainer}>
              {/* Basic Information */}
              <div className={classes.section}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>info</Icon>
                  {translate('general_information')}
                </h3>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('name')}:</span>
                  <span className={classes.value}>{organisation.name}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('contact_email')}:</span>
                  <span className={classes.value}>{organisation.contactEmail || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('city')}:</span>
                  <span className={classes.value}>{organisation.city || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('country')}:</span>
                  <span className={classes.value}>{organisation.country || '-'}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('total_devices')}:</span>
                  <span className={classes.value}>{organisation.deviceCount || 0}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>Subscription:</span>
                  <span className={classes.value}>
                    <span style={{
                      backgroundColor: organisation.subscriptionStatus?.isActive ? '#e8f5e9' : '#fff3e0',
                      color: organisation.subscriptionStatus?.isActive ? '#2e7d32' : '#ff9800',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      display: 'inline-block',
                      marginRight: '8px'
                    }}>
                      {organisation.subscriptionStatus?.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {organisation.subscriptionStatus?.isActive && organisation.subscriptionStatus?.subscriptionId && (
                      <a
                        href={`/subscriptions/${organisation.subscriptionStatus.subscriptionId}`}
                        style={{ fontSize: '12px', color: '#005795', textDecoration: 'none' }}
                      >
                        View Details →
                      </a>
                    )}
                  </span>
                </div>
              </div>

              {/* Record Information */}
              <div className={classes.section}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>storage</Icon>
                  {translate('record_information')}
                </h3>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('organisation_id')}:</span>
                  <span className={classes.value}>{organisation.id}</span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('created_date')}:</span>
                  <span className={classes.value}>
                    {this.formatDate(organisation.createdDate)}
                  </span>
                </div>
                <div className={classes.detailRow}>
                  <span className={classes.label}>{translate('modified_date')}:</span>
                  <span className={classes.value}>
                    {this.formatDate(organisation.modifiedDate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Organisation Preferences */}
            <div className={classes.section} style={{ marginTop: '30px' }}>
              <h3 className={classes.sectionTitle}>
                <Icon style={{ marginRight: '8px', fontSize: '20px' }}>tune</Icon>
                {translate('organisation_preferences')}
              </h3>
              <div className={classes.preferencesGrid}>
                <div>
                  <h4 className={classes.preferencesHeading}>{translate('organisation_contact_details')}</h4>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('contact_name')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue([organisation.contactFirstname, organisation.contactLastname].filter(Boolean))}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('telephoneno')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.telephoneNo)}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_address')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue([organisation.address, organisation.city, organisation.state, organisation.zipCode, organisation.country].filter(Boolean))}</span>
                  </div>
                </div>
                <div>
                  <h4 className={classes.preferencesHeading}>{translate('organisation_report_preferences')}</h4>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('language')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.language)}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_report_measurement')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.chartMeasurement)}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_report_chart_style')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.chartLineStyle)}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_wiremap_preference')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.wireMapDisplayPreference)}</span>
                  </div>
                </div>
                <div>
                  <h4 className={classes.preferencesHeading}>{translate('organisation_profile')}</h4>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_activities')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.organisationActivities)}</span>
                  </div>
                  {organisation.organisationActivityOther && (
                    <div className={classes.detailRow}>
                      <span className={classes.label}>{translate('organisation_other_activity')}:</span>
                      <span className={classes.value}>{organisation.organisationActivityOther}</span>
                    </div>
                  )}
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_field_team_size')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.fieldTechnicianRange)}</span>
                  </div>
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_equipment_use')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.equipmentUsage)}</span>
                  </div>
                  {organisation.equipmentUsageOther && (
                    <div className={classes.detailRow}>
                      <span className={classes.label}>{translate('organisation_other_equipment_use')}:</span>
                      <span className={classes.value}>{organisation.equipmentUsageOther}</span>
                    </div>
                  )}
                  <div className={classes.detailRow}>
                    <span className={classes.label}>{translate('organisation_testing_applications')}:</span>
                    <span className={classes.value}>{this.renderPreferenceValue(organisation.testingApplications)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Flags Section */}
            <div className={classes.section} style={{ marginTop: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>flag</Icon>
                  {translate('feature_flags')}
                </h3>
                <DialogTrigger>
                  <ActionButton>
                    Grant Feature Access
                  </ActionButton>
                  {(close) => (
                    <Dialog>
                      <Heading>Grant Feature Access</Heading>
                      <Divider />
                      <SpectrumContent>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                              Select Feature
                            </label>
                            <select
                              value={selectedFeature}
                              onChange={(e) => this.setState({ selectedFeature: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="">-- Choose a feature --</option>
                              {featureDefinitions && Array.isArray(featureDefinitions) && featureDefinitions.length > 0 ? (
                                featureDefinitions.map(feature => (
                                  <option key={feature.name} value={feature.name}>
                                    {feature.displayName} ({feature.category})
                                  </option>
                                ))
                              ) : (
                                <option disabled>No features available</option>
                              )}
                            </select>
                          </div>

                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                              Access Type
                            </label>
                            <label style={{ display: 'block', marginBottom: '8px' }}>
                              <input
                                type="radio"
                                name="featureAccessType"
                                value="trial"
                                checked={selectedAccessType === 'trial'}
                                onChange={(e) => this.setState({ selectedAccessType: e.target.value })}
                                style={{ marginRight: '8px' }}
                              />
                              Trial access
                            </label>
                            <label style={{ display: 'block' }}>
                              <input
                                type="radio"
                                name="featureAccessType"
                                value="indefinite"
                                checked={selectedAccessType === 'indefinite'}
                                onChange={(e) => this.setState({ selectedAccessType: e.target.value })}
                                style={{ marginRight: '8px' }}
                              />
                              Indefinite access
                            </label>
                          </div>

                          {selectedAccessType === 'trial' && (
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                              Duration (days)
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={selectedDays}
                              onChange={(e) => this.setState({ selectedDays: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                              }}
                            />
                          </div>
                          )}

                          {error && (
                            <div style={{
                              backgroundColor: '#ffebee',
                              color: '#c62828',
                              padding: '12px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {error}
                            </div>
                          )}
                        </div>
                      </SpectrumContent>
                      <ButtonGroup>
                        <SpectrumButton variant="secondary" onPress={close}>
                          Cancel
                        </SpectrumButton>
                        <SpectrumButton
                          variant="accent"
                          onPress={() => {
                            this.handleGrantFeature()
                            close()
                          }}
                          isDisabled={grantingFeature}
                        >
                          {grantingFeature ? 'Granting...' : 'Grant Feature'}
                        </SpectrumButton>
                      </ButtonGroup>
                    </Dialog>
                  )}
                </DialogTrigger>
              </div>

              {error && (
                <div style={{
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              {Object.keys(enrichedFeatures).length > 0 ? (
                <div style={{ marginBottom: '16px' }}>
                  <TableView aria-label="Feature flags">
                      <TableHeader>
                        <Column isRowHeader width={300}>Feature</Column>
                        <Column>Category</Column>
                        <Column>Status</Column>
                        <Column>Expiry Date</Column>
                        <Column>Type</Column>
                        <Column>Actions</Column>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(enrichedFeatures).flatMap(([category, features]) =>
                          features.map((feature, idx) => (
                            <Row key={`${feature.name}-${idx}`} id={`feature-${feature.name}`}>
                              <Cell>{feature.displayName}</Cell>
                              <Cell>{category}</Cell>
                              <Cell>
                                <Icon
                                  style={{
                                    color: feature.isActive ? '#7cb342' : '#bdbdbd',
                                    fontSize: '20px'
                                  }}
                                  aria-label={feature.isActive ? 'Active' : 'Inactive'}
                                >
                                  {feature.isActive ? 'check_circle' : 'cancel'}
                                </Icon>
                              </Cell>
                              <Cell>
                                {feature.isActive && feature.expiryDate ? (
                                  <div>
                                    <div>{feature.reason === 'manual' ? 'Indefinite' : this.formatDate(feature.expiryDate)}</div>
                                    {feature.reason !== 'manual' && feature.daysRemaining >= 0 && (
                                      <div style={{ fontSize: '12px', color: feature.daysRemaining <= 7 ? '#f57c00' : '#666' }}>
                                        {feature.daysRemaining} days remaining
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: '#999' }}>-</span>
                                )}
                              </Cell>
                              <Cell>
                                {feature.isActive && (
                                  <span style={{
                                    backgroundColor: feature.reason === 'subscription' ? '#c8e6c9' : feature.reason === 'manual' ? '#e3f2fd' : '#fff9c4',
                                    color: feature.reason === 'subscription' ? '#2e7d32' : feature.reason === 'manual' ? '#1565c0' : '#f57f17',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 500
                                  }}>
                                    {feature.reason === 'subscription' ? 'Subscription' : feature.reason === 'manual' ? 'Manual' : 'Trial'}
                                  </span>
                                )}
                              </Cell>
                              <Cell>
                                {feature.isActive && feature.reason !== 'subscription' && (
                                  <DialogTrigger>
                                    <ActionButton>Revoke</ActionButton>
                                    {(close) => (
                                      <AlertDialog
                                        title="Revoke Feature?"
                                        variant="warning"
                                        primaryActionLabel="Revoke"
                                        secondaryActionLabel="Cancel"
                                        onPrimaryAction={() => {
                                          this.handleConfirmRevoke(feature.name)
                                          close()
                                        }}
                                      >
                                        Are you sure you want to revoke the <strong>{feature.displayName}</strong> feature?
                                        This action cannot be undone, and the organisation will immediately lose access to this feature.
                                      </AlertDialog>
                                    )}
                                  </DialogTrigger>
                                )}
                              </Cell>
                            </Row>
                          ))
                        )}
                      </TableBody>
                    </TableView>
                </div>
              ) : (
                <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', color: '#999', textAlign: 'center' }}>
                  <Icon style={{ fontSize: '32px', color: '#ccc', marginBottom: '8px', display: 'block' }}>flag</Icon>
                  <p style={{ margin: 0 }}>No features available</p>
                </div>
              )}
            </div>

            {/* Users Section */}
            {organisation.users && organisation.users.length > 0 && (
              <div className={classes.section} style={{ marginTop: '30px' }}>
                <h3 className={classes.sectionTitle}>
                  <Icon style={{ marginRight: '8px', fontSize: '20px' }}>people</Icon>
                  {translate('general_users')}
                </h3>
                <div>
                  <TableView aria-label="User accounts">
                    <TableHeader>
                      <Column isRowHeader>{translate('name')}</Column>
                      <Column>{translate('contact_email')}</Column>
                      <Column>{translate('status')}</Column>
                      <Column>{translate('general_date')}</Column>
                    </TableHeader>
                    <TableBody>
                      {organisation.users.map((user, index) => (
                        <Row key={`user-${user.id || index}`} id={`user-${user.id || index}`}>
                          <Cell>{user.name || '-'}</Cell>
                          <Cell>{user.email || '-'}</Cell>
                          <Cell>
                            <span style={{
                              backgroundColor: user.isActive ? '#e8f5e9' : '#fff3e0',
                              color: user.isActive ? '#4caf50' : '#ff9800',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </Cell>
                          <Cell>{this.formatDate(user.createdDate)}</Cell>
                        </Row>
                      ))}
                    </TableBody>
                    </TableView>
                </div>
              </div>
            )}

            {/* Empty State for Users */}
            {(!organisation.users || organisation.users.length === 0) && (
              <div className={classes.emptyState} style={{ marginTop: '30px' }}>
                <Icon style={{ fontSize: '48px', color: '#ccc' }}>people</Icon>
                <p style={{ color: '#999' }}>{translate('no_users')}</p>
              </div>
            )}
          </div>
        </Content>
        </div>
      </Provider>
    )
  }
}

export default connect(
  (state) => {
    return {
      organisationDetail: state.organisationDetail
    }
  }
)(withStyles(styles)(OrganisationDetail))
